import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useExams, useAddExam, useDeleteExam } from '@/services/student';
import { useTimetable } from '@/services/children';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { CountdownChip } from '@/components/ui/CountdownChip';
import { Spinner } from '@/components/ui/Spinner';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { GlobalHeader } from '@/components/GlobalHeader';

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exam = new Date(dateStr);
  exam.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((exam.getTime() - today.getTime()) / 86_400_000));
}

export function ExamsPage() {
  const { t } = useTranslation();
  const { childId } = useAuthStore();
  const { data: exams, isLoading } = useExams(childId ?? '');
  const { mutateAsync: addExam, isPending: adding } = useAddExam(childId ?? '');
  const { mutateAsync: deleteExam } = useDeleteExam(childId ?? '');
  const { data: timetable } = useTimetable(childId ?? '');

  const [sheetOpen, setSheetOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState('');

  const timetableSubjects = Array.from(
    new Set(timetable?.entries.map((e) => e.subject) ?? []),
  );

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (exams ?? []).filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = (exams ?? []).filter((e) => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await addExam({ subject, name, date });
      setSubject('');
      setName('');
      setDate('');
      setSheetOpen(false);
    } catch {
      setError(t('exams.add_error'));
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <div className="flex items-center justify-between" style={{ marginBottom: '20px' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
              {t('exams.title')}
            </h1>
            <Button size="sm" onClick={() => setSheetOpen(true)}>
              {t('exams.add_exam_btn')}
            </Button>
          </div>

          {isLoading && <Spinner size="lg" className="text-primary-600" />}

          {!isLoading && upcoming.length === 0 && past.length === 0 && (
            <Card className="py-10 text-center">
              <p className="text-gray-500">{t('exams.no_exams')}</p>
              <Button className="mt-3" size="sm" onClick={() => setSheetOpen(true)}>
                {t('exams.add_first')}
              </Button>
            </Card>
          )}

          {upcoming.length > 0 && (
            <div className="flex flex-col gap-2" style={{ marginBottom: '16px' }}>
              {upcoming.map((exam) => {
                const days = daysUntil(exam.date);
                return (
                  <Card key={exam.exam_id} className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{exam.name}</p>
                      <p className="text-sm text-gray-500">{exam.subject}</p>
                      <p className="mt-0.5 text-xs text-gray-400">
                        {new Date(exam.date).toLocaleDateString(undefined, { dateStyle: 'long' })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <CountdownChip daysLeft={days} label={exam.name} />
                      <button
                        aria-label={t('exams.delete_aria', { name: exam.name })}
                        onClick={() => deleteExam(exam.exam_id)}
                        className="text-xs text-gray-400 hover:text-danger-600"
                      >
                        {t('exams.remove')}
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {past.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer select-none text-sm font-medium text-gray-400 hover:text-gray-600">
                {t('exams.past_exams', { count: past.length })}
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                {past.map((exam) => (
                  <Card key={exam.exam_id} className="flex items-start justify-between gap-3 opacity-60">
                    <div>
                      <p className="font-medium text-gray-700">{exam.name}</p>
                      <p className="text-sm text-gray-400">{exam.subject}</p>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(exam.date).toLocaleDateString(undefined, { dateStyle: 'short' })}
                    </p>
                  </Card>
                ))}
              </div>
            </details>
          )}

          <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('exams.sheet_title')}>
            <form onSubmit={handleAdd} className="flex flex-col gap-4">
              {timetableSubjects.length > 0 ? (
                <Select
                  label={t('exams.subject')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                >
                  <option value="">{t('exams.select_subject')}</option>
                  {timetableSubjects.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              ) : (
                <Input
                  label={t('exams.subject')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('exams.subject_placeholder')}
                  required
                  maxLength={200}
                />
              )}
              <Input
                label={t('exams.exam_name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('exams.name_placeholder')}
                required
                maxLength={200}
              />
              <Input
                label={t('exams.date')}
                type="date"
                value={date}
                min={today}
                onChange={(e) => setDate(e.target.value)}
                required
              />
              {error && <p className="text-sm text-danger-700">{error}</p>}
              <Button type="submit" loading={adding} className="w-full">
                {t('exams.sheet_title')}
              </Button>
            </form>
          </BottomSheet>

        </div>
        <BottomTabBar activeTab="Exams" />
      </div>
    </div>
  );
}
